import { datadogRum, DataDogSeverityLevel } from '@/lib/DatadogService';
import { ErrorInfo, ReactNode, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ComponentFailurePlaceholderBlock, {
  ComponentFailurePlaceholderBlockProps
} from '../ErrorFallback/ComponentFailurePlaceholderBlock';
import { ComponentFailureVariant } from '../ErrorFallback/util';
import { useNavigate } from 'react-router-dom';

export type withPliErrorBoundaryProps = ComponentFailurePlaceholderBlockProps & {
  onLogError?: (error: Error, errorInfo: ErrorInfo) => void;
  onButtonClick?: () => void;
  fallback?: ReactNode;
  logSecurityLevel?: DataDogSeverityLevel;
  name?: string;
  variant?: ComponentFailureVariant;
};

/**
 * Higher-order component (HOC) that wraps a given component with an error boundary.
 * Provides error logging, customizable fallback UI, and retry functionality.
 *
 * @template P - Props type of the wrapped component.
 * @param WrappedComponent - The React component to be wrapped with the error boundary.
 * @param extraProps - Optional props for error handling, logging, fallback UI, and retry button.
 * @returns A new component with error boundary capabilities.
 *
 * @remarks
 * **IMPORTANT: when `resetErrorBoundary()` is called from `fallbackComponent` the wrapped component is re-rendered**
 * - Uses `ErrorBoundary` to catch errors in the wrapped component tree.
 * - Supports logging errors via `appInsights` and an optional `onLogError` callback.
 * - Allows customization of the fallback UI via the `fallback` prop.
 * - Provides a retry mechanism through the `onRetryButtonClick` prop.
 * - The fallback component structure:
 *   - If `fallback` is provided, it is rendered as the error UI.
 *   - Otherwise, renders `ComponentFailurePlaceholderBlock` with:
 *     - All original props and extra error boundary props.
 *     - `sectionClassNames` and `containerClassNames` for layout customization.
 *     - `onRetryButtonClick` triggers both error boundary reset and any provided retry logic.
 *
 * @example
 * ```tsx
 * const SafeComponent = withPliErrorBoundary(MyComponent, {
 *   fallback: <CustomFallback />,
 *   onLogError: (error, info) => { ... },
 *   onRetryButtonClick: () => { ... },
 *   sectionClassNames: "w-full flex justify-center",
 *   containerClassNames: "max-w-lg mx-auto"
 * });
 * ```
 */
const withPliErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  extraProps?: withPliErrorBoundaryProps
) => {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const WithErrorBoundary: React.FC<P> = props => {
    const navigate = useNavigate();
    const { onLogError, fallback, logSecurityLevel } = extraProps || {};

    const componentProps = props as withPliErrorBoundaryProps;
    const onRetryClick = componentProps?.onRetryButtonClick ?? extraProps?.onRetryButtonClick;
    const variant = componentProps?.variant ?? extraProps?.variant;

    const onButtonClick = useCallback(() => {
      if (onRetryClick) onRetryClick();
      else navigate(0);
    }, [navigate, onRetryClick]);

    const onError = useCallback(
      (error: Error, errorInfo: ErrorInfo) => {
        datadogRum.addError(error, {
          severityLevel: logSecurityLevel ?? DataDogSeverityLevel.Error
        });

        onLogError?.(error, errorInfo);
      },
      [logSecurityLevel, onLogError]
    );

    const fallbackComponent = (fallbackProps: { error: Error; resetErrorBoundary: () => void }) => {
      return (
        fallback ??
        componentProps?.fallback ?? (
          <ComponentFailurePlaceholderBlock
            {...props}
            {...extraProps}
            variant={variant}
            onRetryButtonClick={() => {
              fallbackProps.resetErrorBoundary();
              onButtonClick();
            }}
          />
        )
      );
    };

    return (
      <ErrorBoundary FallbackComponent={fallbackComponent} onError={onError}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };

  WithErrorBoundary.displayName = `withPliErrorBoundary(${displayName})`;

  return WithErrorBoundary;
};

export default withPliErrorBoundary;
